/**
 * useDisplayStream — hook realtime pour la vue display (tablette kiosk)
 *
 * Stratégie :
 *   1. Snapshot initial via GET /api/display/:slug
 *   2. Abonnement PocketBase natif sur `display_queue` (viewRule = "" → broadcast
 *      garanti même pour les clients anonymes).
 *      Toute insertion / suppression dans la file déclenche un refetch.
 *   3. Abonnement au topic custom `display.{slug}` pour les changements de
 *      réglages du board (la collection `boards` est filtrée par auth).
 *
 * Plus de hooks SSE propriétaires. PocketBase gère tout.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import type { UnsubscribeFunc } from "pocketbase";
import { pb } from "@/lib/pb";
import type { FeedPayload } from "@/lib/display-feed";

type StreamStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error"
  | "forbidden"
  | "not_found";

interface UseDisplayStreamOptions {
  slug: string;
  authToken?: string;
  onFeed: (feed: FeedPayload) => void;
}

export function useDisplayStream({ slug, authToken, onFeed }: UseDisplayStreamOptions) {
  const [status, setStatus] = useState<StreamStatus>("connecting");
  const hasEverLoaded = useRef(false);
  const onFeedRef = useRef(onFeed);
  onFeedRef.current = onFeed;

  const fetchFeed = useCallback(
    async (): Promise<"loaded" | "auth" | "forbidden" | "not_found" | "error"> => {
    try {
      const encodedSlug = encodeURIComponent(slug);
      const endpoint = authToken
        ? `/api/display/${encodedSlug}?auth=${encodeURIComponent(authToken)}`
        : `/api/display/${encodedSlug}`;
      const data = await pb.send<{ ok: boolean; feed?: FeedPayload }>(
        endpoint,
        { method: "GET" },
      );
      if (data.ok && data.feed) {
        hasEverLoaded.current = true;
        setStatus("connected");
        onFeedRef.current(data.feed);
        return "loaded";
      }
      setStatus("error");
      return "error";
    } catch (e: unknown) {
      const s = (e as { status?: number }).status ?? 0;
      if (s === 401) {
        setStatus("forbidden");
        return "auth";
      }
      if (s === 403) {
        setStatus("forbidden");
        return "forbidden";
      }
      if (s === 404) {
        setStatus("not_found");
        return "not_found";
      }
      setStatus("error");
      return "error";
    }
  }, [slug, authToken]);

  useEffect(() => {
    const unsubs: UnsubscribeFunc[] = [];
    let cancelled = false;

    async function start() {
      // 1. Snapshot initial
      const result = await fetchFeed();
      if (cancelled) return;
      if (result === "auth" || result === "forbidden" || result === "not_found") return;

      // Debounce commun aux deux abonnements
      let debounceTimer: ReturnType<typeof setTimeout> | null = null;
      const handleEvent = () => {
        if (cancelled) return;
        if (debounceTimer) clearTimeout(debounceTimer);
        if (!hasEverLoaded.current) setStatus("connecting");
        debounceTimer = setTimeout(() => {
          fetchFeed().catch(() => {
            if (!cancelled) setStatus("reconnecting");
          });
        }, 300);
      };

      // 2. display_queue — realtime natif PocketBase.
      //    viewRule = "" sur cette collection → les clients anonymes (tablette)
      //    reçoivent les events sans filtrage. Tout insert/delete dans la file
      //    du board déclenche un refetch.
      //    On s'abonne à "*" (tous les records) et on laisse le serveur
      //    renvoyer uniquement les items du bon board via /api/display/:slug.
      try {
        const unsub = await pb.collection("display_queue").subscribe("*", handleEvent);
        if (!cancelled) {
          unsubs.push(unsub);
        } else {
          unsub();
        }
      } catch (err) {
        console.error("[display-stream] subscribe display_queue échoué:", err);
        if (!cancelled) setStatus("reconnecting");
      }

      // 3. Topic custom display.{slug} — changements de réglages du board.
      //    La collection `boards` a listRule/viewRule auth-only, donc la tablette
      //    anonyme ne reçoit pas les events natifs sur boards.
      //    Le hook Go broadcast sur ce topic custom après chaque update de board.
      if (!cancelled) {
        try {
          const unsub = await pb.realtime.subscribe(`display.${slug}`, handleEvent);
          if (!cancelled) {
            unsubs.push(unsub);
          } else {
            unsub();
          }
        } catch (err) {
          console.error("[display-stream] subscribe display. topic échoué:", err);
          // Non fatal — les réglages du board ne seront pas mis à jour en live,
          // mais le contenu continuera à s'afficher normalement.
        }
      }
    }

    start();

    return () => {
      cancelled = true;
      for (const unsub of unsubs) {
        try { unsub(); } catch { /* ignore */ }
      }
    };
  }, [slug, fetchFeed]);

  return { status };
}
